"use client";

import * as React from "react";
import Link from "next/link";
import { Cookie, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "cookies-accepted";

/**
 * Banner de aviso de cookies. Aparece la primera visita (sin clave
 * `cookies-accepted` en localStorage). Tras aceptar, se marca la clave y no
 * vuelve a salir.
 *
 * Nota legal: shazon solo usa almacenamiento técnico imprescindible (JWT,
 * preferencias UI), exento de consentimiento segun art. 22.2 LSSICE, pero
 * informar es buena práctica de transparencia.
 */
export function CookieBanner() {
  const [visible, setVisible] = React.useState(false);
  const t = useTranslations("cookie_banner");

  React.useEffect(() => {
    try {
      const accepted = window.localStorage.getItem(STORAGE_KEY);
      if (!accepted) setVisible(true);
    } catch {
      // localStorage no accesible (modo privado estricto). No mostramos
      // banner: la app probablemente no funciona sin storage de todas formas.
    }
  }, []);

  const accept = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      // ignorar
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-4 md:max-w-md z-50 rounded-lg border bg-background shadow-lg p-4"
      role="dialog"
      aria-label={t("aria_label")}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
          <Cookie className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 text-sm">
          <p className="font-medium mb-1">{t("title")}</p>
          <p className="text-muted-foreground">
            {t("body")}{" "}
            <Link
              href="/politica-cookies"
              className="text-primary hover:underline"
            >
              {t("more_info")}
            </Link>
            .
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={accept}>
              {t("accept")}
            </Button>
            <Link
              href="/politica-cookies"
              className="text-xs text-muted-foreground hover:underline"
            >
              {t("policy_link")}
            </Link>
          </div>
        </div>
        <button
          type="button"
          onClick={accept}
          aria-label={t("close_aria")}
          className="text-muted-foreground hover:text-foreground p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
